import { formatDateWithLocale } from '../../react/shared/utils/formatters'
import { formatCommentTime } from '../../react/shared/comments/commentTime'
import { setNotificationDots } from './notificationDots'

const PANEL_ID = 'notification-menu'

/**
 * The desktop bell's flyover (`notification_bell_menu/1` in
 * notification_bell_components.ex). The shared `data-toggle` handler shows
 * and hides the panel; this loads its content on every open and keeps the
 * bell's `aria-expanded` in step. Register it after `initToggles()`, so a
 * click sees the panel as the toggle left it. Delegated from `document`, so
 * it survives LiveView patches.
 */
export function initNotificationMenu() {
  document.addEventListener('click', syncNotificationMenu)
  document.addEventListener('keydown', closeNotificationMenuOnEscape)
}

// The trigger's aria-expanded is the menu's last known state: a click that
// flips it to open (the bell, not a click-away close) loads the content.
function syncNotificationMenu() {
  const panel = document.getElementById(PANEL_ID)
  const trigger = findTrigger()
  if (!panel || !trigger) {
    return
  }

  const isOpen = !panel.classList.contains('hidden')
  const wasOpen = trigger.getAttribute('aria-expanded') === 'true'
  trigger.setAttribute('aria-expanded', String(isOpen))

  if (isOpen && !wasOpen) {
    void loadNotificationMenu(panel)
  }
}

function closeNotificationMenuOnEscape(event: KeyboardEvent) {
  if (event.key !== 'Escape') {
    return
  }

  const panel = document.getElementById(PANEL_ID)
  if (!panel || panel.classList.contains('hidden')) {
    return
  }

  panel.classList.add('hidden')
  const trigger = findTrigger()
  trigger?.setAttribute('aria-expanded', 'false')
  trigger?.focus()
}

function findTrigger() {
  return document.querySelector<HTMLElement>(`[aria-controls="${PANEL_ID}"]`)
}

// The previous content stays up while refetching; only the first open shows
// the skeleton.
async function loadNotificationMenu(panel: HTMLElement) {
  try {
    const response = await fetch(panel.dataset.url ?? '', { headers: { Accept: 'text/html' } })

    // A lapsed session redirects to the login page, which is no fragment.
    if (!response.ok || response.redirected) {
      throw new Error(`Notification menu failed (status ${response.status})`)
    }

    panel.innerHTML = await response.text()
  } catch {
    showLoadError(panel)
    return
  }

  fillTimes(panel)
  // Loading the menu marked everything seen on the server
  // (NotificationController.menu/2), so the dots go without a reload.
  setNotificationDots(false)
}

function fillTimes(panel: HTMLElement) {
  const locale = document.documentElement.lang || 'en'
  const justNow = panel.dataset.justNow ?? ''

  panel.querySelectorAll<HTMLTimeElement>('time[datetime]').forEach((time) => {
    time.textContent = formatCommentTime(time.dateTime, locale, justNow)
    time.title = formatDateWithLocale(time.dateTime, locale)
  })
}

function showLoadError(panel: HTMLElement) {
  const message = document.createElement('p')
  message.className = 'text-gray-500'
  message.textContent = panel.dataset.errorText ?? ''

  const seeAllLink = document.createElement('a')
  seeAllLink.href = panel.dataset.seeAllUrl ?? ''
  seeAllLink.className = 'flex-none font-semibold text-brand hover:text-brand/80'
  seeAllLink.textContent = panel.dataset.seeAllText ?? ''

  const row = document.createElement('div')
  row.className = 'flex items-center justify-between gap-x-3 px-2.5 py-3 text-sm'
  row.append(message, seeAllLink)

  panel.replaceChildren(row)
}
